import { Modal, Table, Pagination, Tag } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useCallback, useEffect } from "react";
import { MdVisibility } from "react-icons/md";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import { getSocietyAmendments } from "store/admin-slice/adminServicesSlice";
import { Amendment } from "common/interface/Amendment";
import { routes } from "common/routes";

type HistoryRow = Amendment & { key: string };

const statusColorMap: Record<string, string> = {
    PENDING: "gold",
    APPROVED: "green",
    REJECTED: "red",
    CANCELLED: "default",
};

const StyledActionLink = styled(NavLink)`
  border: 1px solid;
  padding: 0.2rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

interface SocietyHistoryModalProps {
    societyId: string | null;
    societyName?: string;
    open: boolean;
    onClose: () => void;
}

export const SocietyHistoryModal = ({ societyId, societyName, open, onClose }: SocietyHistoryModalProps) => {
    const dispatch = useAppDispatch();
    const { amendments, amendmentStatus } = useAppSelector((state) => state.adminServices);

    const getData = useCallback(
        (pageNum = 1, pageSize = 10) => {
            if (!societyId) return;
            dispatch(getSocietyAmendments({
                pageNo: pageNum,
                pageSize,
                entityId: societyId,
            }));
        },
        [dispatch, societyId]
    );

    useEffect(() => {
        if (open && societyId) {
            getData(1, 10);
        }
    }, [open, societyId, getData]);

    const columns: ColumnsType<HistoryRow> = [
        { title: "Request ID", dataIndex: "id", key: "id", width: 90 },
        { title: "Type", dataIndex: "requestType", key: "requestType", width: 110 },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status: string) => (
                <Tag color={statusColorMap[status] ?? "default"}>{status}</Tag>
            ),
        },
        { title: "Requested By", dataIndex: "requestedBy", key: "requestedBy", width: 120 },
        {
            title: "Requested At",
            dataIndex: "requestedAt",
            key: "requestedAt",
            width: 150,
            render: (value: string) => (value ? new Date(value).toLocaleString() : "-"),
        },
        {
            title: "Reviewed By",
            key: "reviewedBy",
            width: 100,
            render: (_: unknown, record: HistoryRow) => record.metadata?.reviewedBy ?? "-",
        },
        {
            title: "Remarks",
            key: "remarks",
            ellipsis: true,
            render: (_: unknown, record: HistoryRow) => record.metadata?.remarks ?? "-",
        },
        {
            title: "",
            key: "actions",
            width: 50,
            render: (_: unknown, record: HistoryRow) => (
                <StyledActionLink
                    to={`/${routes.CLUSTER_SOCIETY.SOCIETY_AMENDMENT_DETAIL.replace(":id", String(record.id))}?from=list&entityId=${societyId}`}
                    onClick={onClose}
                >
                    <MdVisibility />
                </StyledActionLink>
            ),
        },
    ];

    const rows: HistoryRow[] = (amendments?.data ?? []).map((item, i) => ({
        ...item,
        key: `history-${item.id}-${i}`,
    }));

    return (
        <Modal
            title={`Amendment History${societyName ? ` — ${societyName}` : ""} (#${societyId})`}
            open={open}
            onCancel={onClose}
            footer={null}
            width={900}
            destroyOnClose
        >
            <Table
                columns={columns}
                dataSource={rows}
                size="small"
                pagination={false}
                loading={amendmentStatus === "loading"}
                bordered
                scroll={{ x: 800, y: 400 }}
            />
            {amendments && amendments.total > 0 && (
                <StyledPagination
                    current={amendments.pageNo}
                    pageSize={amendments.pageSize}
                    total={amendments.total}
                    size="small"
                    onChange={(page, size) => getData(page, size)}
                />
            )}
            {!rows.length && amendmentStatus !== "loading" && (
                <p style={{ marginTop: "1rem" }}>No amendment history found.</p>
            )}
        </Modal>
    );
};
